/**
 * Sibling legend for `StateDonut`. Renders one row per state with a
 * color swatch + label + count + percentage. Zero-count states are
 * dimmed (kept in the list so the legend stays predictable across
 * snapshots) rather than hidden.
 */
import React from "react";

import { STACKED_HEX, STATE_HEX } from "../../lib/colors";

import type { DashboardBucket } from "./StateDonut";

const BUCKET_LABEL: Record<DashboardBucket, string> = {
  UP: "Up",
  SCANNER_ONLY: "Scanner-only",
  CAMERA_ONLY: "Camera-only",
  UNKNOWN: "Unknown",
  DOWN: "Down",
  POWERED_DOWN: "Powered-down",
  UNTOUCHED: "Untouched",
  STACKED: "Stacked",
};

const ORDER: DashboardBucket[] = [
  "UP",
  "SCANNER_ONLY",
  "CAMERA_ONLY",
  "UNKNOWN",
  "DOWN",
  "POWERED_DOWN",
  "UNTOUCHED",
  "STACKED",
];

function bucketColor(b: DashboardBucket): string {
  return b === "STACKED" ? STACKED_HEX : STATE_HEX[b];
}

export const StateLegend: React.FC<{
  counts: Partial<Record<DashboardBucket, number>>;
}> = ({ counts }) => {
  const total = ORDER.reduce((s, b) => s + (counts[b] ?? 0), 0);
  return (
    <ul className="space-y-1.5 text-sm">
      {ORDER.map((b) => {
        const v = counts[b] ?? 0;
        const pct = total > 0 ? Math.round((v / total) * 100) : 0;
        const dim = v === 0;
        return (
          <li
            key={b}
            className={
              "flex items-center gap-2 " + (dim ? "opacity-40" : "")
            }
          >
            <span
              className="inline-block w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: bucketColor(b) }}
              aria-hidden
            />
            <span className="flex-1 text-secondary-700">{BUCKET_LABEL[b]}</span>
            <span className="font-mono font-semibold text-secondary-900">
              {v}
            </span>
            <span className="text-secondary-500 text-xs w-10 text-right">
              {pct}%
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default StateLegend;
